/**
 * Analytics and Conversion Tracking Utility
 * Handles Google Analytics, Facebook Pixel, and custom events
 */

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  // Google Analytics / GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'page_view',
      'page_path': pagePath,
      'page_title': pageTitle
    });
  }

  // PostHog
  if (window.posthog) {
    window.posthog.capture('$pageview', {
      '$current_url': window.location.href,
      'page_title': pageTitle
    });
  }
};

// Track button clicks
export const trackButtonClick = (buttonName, buttonLocation, additionalData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'button_click',
      'button_name': buttonName,
      'button_location': buttonLocation,
      ...additionalData
    });
  }

  if (window.posthog) {
    window.posthog.capture('button_click', {
      button_name: buttonName,
      button_location: buttonLocation,
      ...additionalData
    });
  }
};

// Track form submissions
export const trackFormSubmission = (formName, formData = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'form_submission',
      'form_name': formName,
      ...formData
    });
  }

  if (window.posthog) {
    window.posthog.capture('form_submission', {
      form_name: formName,
      ...formData
    });
  }
};

// Track signup intent
export const trackSignupIntent = (source, medium = 'website') => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'signup_intent',
      'signup_source': source,
      'signup_medium': medium
    });
  }

  if (window.posthog) {
    window.posthog.capture('signup_intent', {
      source: source,
      medium: medium
    });
  }
};

// Track demo account request
export const trackDemoRequest = (source) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'demo_request',
      'demo_source': source
    });
  }

  if (window.posthog) {
    window.posthog.capture('demo_request', {
      source: source
    });
  }
};

// Track download events
export const trackDownload = (platform, downloadType) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'platform_download',
      'platform': platform,
      'download_type': downloadType
    });
  }

  if (window.posthog) {
    window.posthog.capture('platform_download', {
      platform: platform,
      download_type: downloadType
    });
  }
};

// Track contact form submission
export const trackContactSubmission = (subject) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'contact_form_submission',
      'contact_subject': subject
    });
  }

  if (window.posthog) {
    window.posthog.capture('contact_form_submission', {
      subject: subject
    });
  }
};

// Track lead capture
export const trackLeadCapture = (source, email = false) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'lead_capture',
      'lead_source': source,
      'email_provided': email
    });
  }

  if (window.posthog) {
    window.posthog.capture('lead_capture', {
      source: source,
      email_provided: email
    });
  }
};

// Track scroll depth
export const trackScrollDepth = (percentage) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'scroll_depth',
      'scroll_percentage': percentage
    });
  }
};

// Track time on page
export const trackTimeOnPage = (seconds) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'time_on_page',
      'time_seconds': seconds
    });
  }
};

// UTM Parameter Handler
export const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  };
};

// Store UTM params in session
export const storeUTMParams = () => {
  const utmParams = getUTMParams();
  if (Object.values(utmParams).some(v => v !== null)) {
    sessionStorage.setItem('td_utm_params', JSON.stringify(utmParams));
  }
};

// Get stored UTM params
export const getStoredUTMParams = () => {
  const stored = sessionStorage.getItem('td_utm_params');
  return stored ? JSON.parse(stored) : null;
};

// Build signup URL with tracking params
export const buildSignupURL = (baseURL, source, additionalParams = {}) => {
  const url = new URL(baseURL);
  const utmParams = getStoredUTMParams() || {};
  
  // Add UTM params
  if (utmParams.utm_source) url.searchParams.set('utm_source', utmParams.utm_source);
  if (utmParams.utm_medium) url.searchParams.set('utm_medium', utmParams.utm_medium);
  if (utmParams.utm_campaign) url.searchParams.set('utm_campaign', utmParams.utm_campaign);
  
  // Add source
  url.searchParams.set('ref', source);
  
  // Add additional params
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  
  return url.toString();
};

export default {
  trackPageView,
  trackButtonClick,
  trackFormSubmission,
  trackSignupIntent,
  trackDemoRequest,
  trackDownload,
  trackContactSubmission,
  trackLeadCapture,
  trackScrollDepth,
  trackTimeOnPage,
  getUTMParams,
  storeUTMParams,
  getStoredUTMParams,
  buildSignupURL
};
